/**
 * Native Bridge — Abstracts Capacitor plugins for cross-platform use.
 * In web mode, these gracefully degrade to browser APIs or no-ops.
 */
import { Capacitor } from '@capacitor/core';
import { Network } from '@capacitor/network';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { App as CapApp } from '@capacitor/app';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Device } from '@capacitor/device';
import type { DeviceInfo as AutoProvisionDeviceInfo } from '@caro/shared/types';

export const isNative = Capacitor.isNativePlatform();
export const platform = Capacitor.getPlatform(); // 'web' | 'android' | 'ios'

/**
 * Network status utilities
 */
export const NativeBridge = {
  /** Check if device has network connectivity */
  async getNetworkStatus() {
    try {
      const status = await Network.getStatus();
      return {
        connected: status.connected,
        connectionType: status.connectionType,
      };
    } catch {
      return { connected: navigator.onLine, connectionType: 'unknown' as const };
    }
  },

  /** Listen for network status changes */
  onNetworkChange(callback: (connected: boolean) => void) {
    return Network.addListener('networkStatusChange', (status) => {
      callback(status.connected);
    });
  },

  /** Haptic feedback for emergency interactions */
  async hapticImpact(style: 'heavy' | 'medium' | 'light' = 'heavy') {
    if (!isNative) return;
    try {
      const map = { heavy: ImpactStyle.Heavy, medium: ImpactStyle.Medium, light: ImpactStyle.Light };
      await Haptics.impact({ style: map[style] });
    } catch {
      // Haptics not available
    }
  },

  /** Vibrate for emergency countdown ticks */
  async vibrate() {
    if (!isNative) {
      // Browser fallback
      navigator.vibrate?.(200);
      return;
    }
    try {
      await Haptics.vibrate();
    } catch {
      navigator.vibrate?.(200);
    }
  },

  /** Set status bar style */
  async setStatusBar(options: { backgroundColor?: string; dark?: boolean }) {
    if (!isNative) return;
    try {
      if (options.backgroundColor) {
        await StatusBar.setBackgroundColor({ color: options.backgroundColor });
      }
      await StatusBar.setStyle({ style: options.dark ? Style.Dark : Style.Light });
    } catch {
      // StatusBar not available
    }
  },

  /** Hide status bar (for emergency full-screen) */
  async hideStatusBar() {
    if (!isNative) return;
    try {
      await StatusBar.hide();
    } catch {
      // Not available
    }
  },

  /** Show status bar */
  async showStatusBar() {
    if (!isNative) return;
    try {
      await StatusBar.show();
    } catch {
      // Not available
    }
  },

  /** Open native dialer for emergency (fallback when SIP unavailable) */
  openDialer(number: string) {
    window.location.href = `tel:${number}`;
  },

  /**
   * Collect the device second factor (serial / UDID) via the Capacitor Device
   * plugin. Degrades to an empty object on web.
   *
   * NOTE (R1, 2026-06-02): auto-provisioning now keys on the Hexnode UDID
   * supplied in the managed-launch URL (`?hexnodeUDID=<udid>`), so
   * `useAutoProvision` NO LONGER calls this method. It is retained as a harmless
   * native utility. The `@capacitor/device` dependency is now unused by the
   * provisioning flow (left in place for this pass; safe to prune later if no
   * other consumer adopts it).
   */
  async getDeviceInfo(): Promise<AutoProvisionDeviceInfo> {
    const info: AutoProvisionDeviceInfo = {};
    try {
      const id = await Device.getId();
      // Capacitor exposes a stable per-install/device identifier here.
      if (id?.identifier) info.udid = id.identifier;
    } catch {
      // Device plugin unavailable (web) — leave udid unset.
    }
    try {
      const di = await Device.getInfo();
      // `serial` is only populated on supervised/managed native devices.
      const serial = (di as { serial?: string }).serial;
      if (serial) info.serial = serial;
    } catch {
      // Not available — leave serial unset.
    }
    return info;
  },

  /** Register app state change listener (background/foreground) */
  onAppStateChange(callback: (isActive: boolean) => void) {
    return CapApp.addListener('appStateChange', (state) => {
      callback(state.isActive);
    });
  },

  /** Register back button handler (Android) */
  onBackButton(callback: () => void) {
    return CapApp.addListener('backButton', () => {
      callback();
    });
  },

  /** Set screen brightness (0–100). Uses native APIs when available. */
  async setBrightness(value: number) {
    const normalized = value / 100;
    // Electron (Linux kiosk) — brightnessctl via IPC
    if ((window as any).electronAPI?.setBrightness) {
      await (window as any).electronAPI.setBrightness(normalized);
      return;
    }
    // Capacitor (Android/iOS) — add @capacitor-community/screen-brightness when needed
    // For now, CSS DimOverlay handles brightness on all platforms
  },

  /** Get current screen brightness (0–100). Returns 100 on web fallback. */
  async getBrightness(): Promise<number> {
    // Electron
    if ((window as any).electronAPI?.getBrightness) {
      const val = await (window as any).electronAPI.getBrightness();
      return Math.round(val * 100);
    }
    // Web fallback — no native brightness available, CSS overlay handles dimming
    return 100;
  },
};
